import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, CalendarClock, BarChart3, Columns3, CalendarDays, Trash2, Settings } from 'lucide-react';
import { useAuth, canModuleTab, isRestricted } from '@/hooks/useAuth';

/**
 * The Leads sub-menu, repeated on every screen in the module.
 *
 * Each entry is gated on its OWN permission tab, the same string the backend route checks — so a
 * role granted only Manage Leads sees one tab here and gets a 403 from anything else, instead of
 * being shown a link that will fail when clicked.
 */
const TABS: { to: string; label: string; tab: string; icon: JSX.Element; adminOnly?: boolean }[] = [
  { to: '/leads/dashboard', label: 'Dashboard', tab: 'Dashboard', icon: <LayoutDashboard size={14} /> },
  { to: '/leads/manage', label: 'Manage Leads', tab: 'Manage Leads', icon: <Users size={14} /> },
  { to: '/leads/followups', label: 'Manage Followups', tab: 'Followups', icon: <CalendarClock size={14} /> },
  { to: '/leads/reports', label: 'Reports', tab: 'Reports', icon: <BarChart3 size={14} /> },
  { to: '/leads/pipeline', label: 'Lead Pipeline', tab: 'Pipeline', icon: <Columns3 size={14} /> },
  { to: '/leads/calendar', label: 'Calendar', tab: 'Calendar', icon: <CalendarDays size={14} /> },
  { to: '/leads/trash', label: 'Trash', tab: 'Trash', icon: <Trash2 size={14} /> },
  // Admins only. The masters behind this tab (statuses, sources, tags, custom columns) change what
  // every lead screen offers, so it is not a per-user preference — one person renaming a status
  // moves the pipeline for the whole team.
  { to: '/leads/settings', label: 'Settings', tab: 'Settings', icon: <Settings size={14} />, adminOnly: true },
];

export function LeadsSubNav() {
  const { user } = useAuth();
  const restricted = isRestricted(user);
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';
  const visible = TABS
    .filter((t) => !t.adminOnly || isAdmin)
    .filter((t) => !restricted || canModuleTab(user, 'Leads', t.tab));

  return (
    <div className="flex items-center gap-1 flex-wrap bg-slate-100 dark:bg-slate-800 rounded-lg p-1 mb-4 w-fit">
      {visible.map((t) => (
        <NavLink key={t.to} to={t.to}
          className={({ isActive }) =>
            `px-3 py-1.5 text-sm rounded-md transition inline-flex items-center gap-1.5 ${
              isActive ? 'bg-white dark:bg-slate-900 shadow-sm text-emerald-700 font-medium' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
            }`}>
          {t.icon} {t.label}
        </NavLink>
      ))}
    </div>
  );
}
